/**
 * DataHandling Module
 * 
 * Handles API calls, data parsing, and error handling including:
 * - Area-based data loading with debouncing
 * - Retry logic for network requests
 * - Response data parsing and validation
 * - TopoJSON data processing
 * - Provider color mapping
 */

import Logger from '../../lib/logger'
import { MapUtilities } from './utilities'

export class DataHandling {
  constructor(controller) {
    this.controller = controller;
  }

  // Load data for current map area
  async areaLoad(e) {
    try {
      const isMoveEvent = e && (e.type === 'moveend' || e.type === 'zoomend');
      this.controller.showSpinner({ silent: isMoveEvent });

      const bounds = e.target.getBounds();
      const area = this.formatBoundsString(bounds);

      // Build URL using base URL from data attribute
      let url = `${this.getBaseUrl()}?l=${area}`;

      // Add provider filter if active
      const currentFilter = this.controller.getCurrentProviderFilter();
      if (currentFilter) {
        url += `&provider_id=${currentFilter.id}`;
        Logger.debug(`Applying provider filter ${currentFilter.name} (ID: ${currentFilter.id})`)
      }

      // Add show_all parameter based on recent bids filter state
      if (!this.controller.showRecentBidsOnlyValue) {
        url += '&show_all=true';
        Logger.debug('Loading all bids (not filtering by recent)')
      } else {
        Logger.debug('Loading only recent bids (last 6 months)')
      }

      Logger.info('Loading combined map data', { url })

      const data = await this.fetchWithRetry(url);

      // Update addresses/markers
      this.controller.addBids(data.addresses);

      // Update bid providers list (silent background update)
      await this.controller.loadBidProviders(null, { silent: true });

      // Update bid list tab (silent background update)
      await this.controller.loadBidList(null, { silent: true });

      // Update bids CRM with new map bounds
      //this.controller.layerMgmt.triggerBidsCrmWithMapFilter();

      // Handle meta information
      if (data.meta) {
        this.handleResponseMeta(data.meta);
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        Logger.debug('Area load aborted');
        return;
      }
      Logger.error('Area load failed', error, {
        controller: this.controller.constructor.name
      });
      this.handleFetchError(error);
    } finally {
      this.controller.hideSpinner();
    }
  }

  // Format bounds as string for API
  formatBoundsString(bounds) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    return `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`;
  }

  // Get base URL from data attribute
  getBaseUrl() {
    const baseUrl = this.controller.urlValue;
    if (baseUrl) {
      return baseUrl;
    }

    // Fallback: try to build from address ID in current URL
    const pathMatch = window.location.pathname.match(/addresses\/(\d+)/);
    if (pathMatch) {
      return `/addresses/${pathMatch[1]}/map_data`;
    }

    throw new Error('Cannot determine base map data URL');
  }

  // Extract address ID from data attribute or URL (kept for compatibility)
  extractAddressId() {
    // Try to get from URL value (new format: /addresses/{id}/map_data)
    const urlValue = this.controller.urlValue;
    if (urlValue) {
      let match = urlValue.match(/addresses\/(\d+)/);
      if (match) return match[1];

      // Fallback to old format: ?id=123
      match = urlValue.match(/id=(\d+)/);
      if (match) return match[1];
    }

    // Fallback: try to extract from current URL
    const pathMatch = window.location.pathname.match(/addresses\/(\d+)/);
    if (pathMatch) return pathMatch[1];

    throw new Error('Cannot determine address ID');
  }

  // Fetch with retry logic supporting AbortSignal
  async fetchWithRetry(url, options = {}, maxRetries = 3) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
      } catch (error) {
        if (error.name === 'AbortError') throw error; // Rethrow immediately

        if (attempt === maxRetries) throw error;

        Logger.warn(`Fetch attempt ${attempt} failed, retrying...`, {
          error: error.message,
          url
        });
        await this.controller.delay(1000 * attempt); // Exponential backoff
      }
    }
  }

  // Parse response data with fallback handling
  parseResponseData(json) {
    if (json.addresses && json.meta) {
      // New format with meta information
      return {
        addresses: json.addresses,
        meta: json.meta
      };
    } else if (Array.isArray(json)) {
      // Legacy format (array of addresses)
      return {
        addresses: json,
        meta: { bounding_box_adjusted: false, total_count: json.length }
      };
    } else {
      // Fallback for invalid data
      return {
        addresses: [],
        meta: { bounding_box_adjusted: false, total_count: 0 }
      };
    }
  }

  // Handle response meta information
  handleResponseMeta(meta) {
    if (!meta) return;

    if (meta.bounding_box_adjusted) {
      this.controller.showInfoMessage(
        'Search area was too large and has been automatically adjusted to show nearby results.',
        'info'
      );
    }

    // Update badges immediately from meta data (total_bids, total_addresses, etc.)
    if (this.controller.ui && this.controller.ui.updateBadgesFromMeta) {
      this.controller.ui.updateBadgesFromMeta(meta);
    }

    if (meta.total_count === 0 && meta.total_addresses === 0) {
      Logger.debug('No addresses found in the requested area')
    }
  }

  // Handle fetch errors gracefully with improved error categorization
  handleFetchError(error) {
    MapUtilities.handleFetchError(this.controller, error);
  }

  // Handle zoom end events
  handleZoomEnd(e) {
    const currentZoom = this.controller.map.getZoom();
    const currentBounds = this.controller.map.getBounds();

    // Refresh GeoJSON layer if it's visible to prevent display issues
    this.controller.refreshGeoJSONLayer();

    // Always fetch data on zoom (both in and out)
    const zoomDirection = currentZoom < this.controller.previousZoom ? 'out' : 'in';
    Logger.debug(`Zoomed ${zoomDirection} - fetching data`, {
      currentZoom,
      previousZoom: this.controller.previousZoom
    })

    // Clear cache to ensure fresh data
    this.controller.layerMgmt.clearCache();

    this.controller._debouncedAreaLoad(e);

    // Update previous zoom for next comparison
    this.controller.previousZoom = currentZoom;
    this.controller.previousBounds = currentBounds;
  }

  // Handle move end events
  handleMoveEnd(e) {
    // Check if suppression flag is set
    if (this.controller.suppressMoveend) {
      this.controller.suppressMoveend = false; // Reset
      Logger.debug('Moveend suppressed by flag')
      return; // Ignore this moveend
    }

    const currentCenter = this.controller.map.getCenter();
    const currentBounds = this.controller.map.getBounds();

    // Only fetch data if the map center has actually changed (panned)
    // Compare lat/lng with a small tolerance to account for floating point precision
    const centerChanged = Math.abs(currentCenter.lat - this.controller.previousCenter.lat) > 0.0001 ||
      Math.abs(currentCenter.lng - this.controller.previousCenter.lng) > 0.0001;

    const outsideBounds = !this.controller.previousBounds.contains(currentBounds);

    if (centerChanged && outsideBounds) {
      Logger.debug('Map was panned - fetching data')
      this.controller._debouncedAreaLoad(e);
      // Update previous center for next comparison
      this.controller.previousCenter = currentCenter;
      this.controller.previousBounds = currentBounds;
    } else {
      Logger.debug('Map moveend triggered by zoom or within previous bounds - not fetching data')
    }
  }

  // Get location data for TopoJSON
  async getLocationData(stateInfoUrl, stateFileUrl) {
    try {
      // Show spinner while fetching state info and geo data
      this.controller.showSpinner();
      Logger.debug('Fetching state info from', { url: stateInfoUrl })

      const response = await fetch(stateInfoUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch state info: ${response.status} ${response.statusText}`);
      }

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        Logger.warn('State info response is not JSON', { contentType })
      }

      // Get response text first to debug
      const responseText = await response.text();
      Logger.debug('State info response text length', {
        length: responseText.length,
        preview: responseText.substring(0, 200)
      })

      // Try to parse JSON
      let json;
      try {
        json = JSON.parse(responseText);
      } catch (parseError) {
        Logger.error('State info JSON parse error', {
          error: parseError.message,
          responsePreview: responseText.substring(0, 500)
        })
        throw new Error(`Invalid JSON response from state info: ${parseError.message}`);
      }

      await this.getGeoData(json, stateFileUrl);
    } catch (error) {
      Logger.error('Failed to get location data', error, {
        controller: this.controller.constructor.name
      })
      this.controller.showErrorMessage(`Failed to load location data: ${error.message}. Please try again.`);
    } finally {
      // Always hide spinner after attempting to get location data
      this.controller.hideSpinner();
    }
  }

  // Get and parse geojson from URL
  async getGeoData(dados, stateFileUrl) {
    try {
      // Note: Spinner is already shown by getLocationData() which calls this method
      Logger.debug('Fetching geo data info from', { url: stateFileUrl })

      const response = await fetch(stateFileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch geo data info: ${response.status} ${response.statusText}`);
      }

      let json = await response.json();

      // If the response is a wrapper with a file_url (user's production setup), fetch the actual file
      if (json.file_url) {
        Logger.debug('Fetching actual topojson from file_url', { url: json.file_url })
        const fileResponse = await fetch(json.file_url);
        if (!fileResponse.ok) {
          throw new Error(`Failed to fetch topojson file: ${fileResponse.status} ${fileResponse.statusText}`);
        }
        json = await fileResponse.json();
      }

      Logger.debug('Geo data received and parsed', {
        type: json.type,
        hasObjects: !!json.objects
      })

      this.addData(json, dados);
    } catch (error) {
      Logger.error('Failed to get geo data', error, {
        controller: this.controller.constructor.name
      })
      this.controller.showErrorMessage(`Failed to load geographic data: ${error.message}. Please try again.`);
      throw error;
    }
  }

  // Add TopoJSON data to map
  addData(topojson, dados) {
    if (!topojson?.objects?.municipios?.geometries) {
      Logger.error('Invalid topojson data structure', { topojson })
      return;
    }

    const municipios = topojson.objects.municipios.geometries;
    const dadosMunicipios = dados || [];

    Logger.debug('Processing TopoJSON data', {
      municipiosCount: municipios.length,
      dadosCount: dadosMunicipios.length
    });

    // Create lookup map for better performance - try multiple key formats
    const municipioLookup = new Map();
    dadosMunicipios.forEach((municipio, index) => {
      // Try different possible property names for IBGE code
      const ibgeCode = municipio.ibge || municipio.ibge_code || municipio.IBGE || municipio.codigo || municipio.CODIGO;

      if (ibgeCode) {
        // Store with both string and number keys to handle different formats
        const numericCode = parseInt(ibgeCode);
        const stringCode = String(ibgeCode);

        municipioLookup.set(numericCode, municipio);
        municipioLookup.set(stringCode, municipio);

        // Debug first few entries
        if (index < 3) {
          Logger.debug('Sample municipio data', {
            index,
            ibgeCode,
            numericCode,
            stringCode,
            providers: municipio.providers,
            municipio
          });
        }
      }
    });

    let matchedCount = 0;
    let totalProcessed = 0;

    // Process each municipality
    municipios.forEach((mun, index) => {
      const codigo = mun.properties.CODIGO || mun.properties.codigo || mun.id;
      if (!codigo) {
        if (index < 5) Logger.warn(`Municipality ${index} missing ID/CODIGO property`)
        return;
      }

      totalProcessed++;
      const stringCodigo = String(codigo);
      const numericCodigo = parseInt(codigo);

      // Robust matching: check 7rd and 6th digits (Brazil IBGE codes can be 6 or 7 digits)
      let municipioData = municipioLookup.get(stringCodigo) ||
        municipioLookup.get(numericCodigo);

      // If no exact match, try 6-digit prefix match
      if (!municipioData && stringCodigo.length >= 6) {
        const prefix6 = stringCodigo.substring(0, 6);
        municipioData = municipioLookup.get(prefix6) || municipioLookup.get(parseInt(prefix6));
      }

      if (municipioData) {
        matchedCount++;
        const providers = parseInt(municipioData.providers) ||
          parseInt(municipioData.city_provider_services_count) || 0;
        mun.properties.cor = this.getProviderColor(providers);
      } else {
        mun.properties.cor = '#cc0000'; // Default for no data
      }
    });

    Logger.debug('TopoJSON processing complete', {
      totalProcessed,
      matchedCount,
      matchRate: matchedCount > 0 ? `${((matchedCount / totalProcessed) * 100).toFixed(1)}%` : '0%'
    });

    this.controller.geojson.addData(topojson);
  }

  // Get color based on provider count
  getProviderColor(providerCount) {
    let color;

    if (providerCount > 2) {
      color = 'green';
    } else if (providerCount > 0) {
      color = 'blue';
    } else {
      color = '#cc0000'; // Red for no providers
    }

    // Debug color assignment for first few calls
    if (this.controller && this.controller.colorDebugCount !== undefined && this.controller.colorDebugCount < 5) {
      Logger.debug('Provider color assignment', {
        providerCount,
        color,
        logic: providerCount > 2 ? 'green (>2)' : providerCount > 0 ? 'blue (>0)' : 'red (0)'
      });
      this.controller.colorDebugCount++;
    }

    return color;
  }

  // Get colors based on data value (utility function)
  getColors(d) {
    const colorMap = {
      0: '#800026',
      1: '#BD0026',
      2: '#E31A1C',
      3: '#FC4E2A',
      4: '#FD8D3C',
      5: '#FEB24C'
    };

    return colorMap[d] || (d >= 6 ? '#FED976' : '#FFEDA0');
  }

  // Reload markers with provider filter
  async reloadMarkersWithFilter(providerId) {
    try {
      // Track if layer was on map before filtering
      const wasOnMap = this.controller.bids && this.controller.map.hasLayer(this.controller.bids);

      Logger.debug(`Provider filter reload - providerId: ${providerId}, showRecentBidsOnly: ${this.controller.showRecentBidsOnlyValue}`)

      // Clear cache when filter changes
      this.controller.layerMgmt.clearCache();

      const bounds = this.controller.map.getBounds();
      const area = this.formatBoundsString(bounds);

      // Build URL using base URL from data attribute
      let url = `${this.getBaseUrl()}?l=${area}`;

      if (providerId) {
        url += `&provider_id=${providerId}`;
      }

      if (!this.controller.showRecentBidsOnlyValue) {
        url += '&show_all=true';
        Logger.debug('Adding show_all=true parameter (showing all bids)')
      } else {
        Logger.debug('Not adding show_all parameter (showing only recent bids)')
      }

      Logger.info(`Loading combined map data: ${url}`)

      const data = await this.fetchWithRetry(url);

      Logger.debug(`Fetched ${data.addresses.length} addresses and ${data.providers.length} providers`)

      // Clear existing markers
      if (this.controller.bids) {
        this.controller.bids.clearLayers();
      } else {
        this.controller.bids = L.layerGroup();
      }

      // Add filtered bids
      this.controller.addBids(data.addresses);

      // Ensure layer is on map if it was before
      if (wasOnMap && this.controller.bids && !this.controller.map.hasLayer(this.controller.bids)) {
        this.controller.bids.addTo(this.controller.map);
        this.controller.ensureProperLayerOrdering();
        this.controller.updateBidsButtonState();
        Logger.debug('Added filtered markers layer back to map')
      }

      // Don't update providers list here - it will be updated by loadBidProviders()
      // which fetches ALL providers (not just filtered ones)

      Logger.debug(`Loaded ${data.addresses.length} filtered markers`)

    } catch (error) {
      Logger.error('Failed to reload markers with filter', error, {
        controller: this.controller.constructor.name,
        providerId
      })
      throw error;
    }
  }

  /**
   * Fetch bid providers from API for current map area
   * Makes an API call to get providers with bids in the current map bounds
   * 
   * @returns {Promise<Array>} Array of provider objects with bid counts
   * @throws {Error} If map is not properly initialized
   */
  async extractBidProvidersFromMarkers() {
    try {
      // Validate map is properly initialized
      if (!this.controller.map?.getBounds) {
        throw new Error('Map not properly initialized')
      }

      // Get current map bounds
      const bounds = this.controller.map.getBounds();
      const boundsString = this.formatBoundsString(bounds);

      Logger.debug('Fetching bid providers for bounds', { boundsString })
      Logger.debug('DEBUG: showRecentBidsOnly state', {
        showRecentBidsOnly: this.controller.showRecentBidsOnlyValue
      })

      // Make API call to get providers
      let url = `/addresses/bid_providers?l=${boundsString}`;

      // Add show_all parameter based on recent bids filter state
      if (!this.controller.showRecentBidsOnlyValue) {
        url += '&show_all=true';
        Logger.debug('DEBUG: Added show_all=true to URL')
      } else {
        Logger.debug('DEBUG: NOT adding show_all (recent bids only)')
      }

      Logger.debug('DEBUG: Final URL', { url })
      const response = await this.fetchWithRetry(url, { signal: this.controller.abortController?.signal });

      if (!response || !response.providers) {
        Logger.warn('No provider data received from API')
        return [];
      }

      Logger.info(`API returned ${response.providers.length} providers`, {
        providersCount: response.providers.length
      })
      return response.providers;

    } catch (error) {
      Logger.error('Failed to fetch bid providers from API', error, {
        controller: this.controller.constructor.name
      })
      return [];
    }
  }
}